import { useState } from 'react';
import { diverging, inkOn } from '../theme';
import { Tooltip } from './ChartFrame';
import { useWidth } from './scale';

export interface HeatCell {
  row: number;
  col: number;
  value: number;
  n: number;
}

/** Regime × action grid on a diverging blue ↔ red scale with a neutral midpoint. */
export function Heatmap({ rows, cols, cells, minN = 3 }: { rows: string[]; cols: string[]; cells: HeatCell[]; minN?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<HeatCell | null>(null);
  const labelW = 104;
  const gap = 2;
  const cellW = Math.max(36, (width - labelW - gap * cols.length) / cols.length);
  const cellH = 34;
  const maxAbs = Math.max(0.05, ...cells.filter((c) => c.n >= minN).map((c) => Math.abs(c.value)));
  const height = 22 + rows.length * (cellH + gap);
  const at = (r: number, c: number) => cells.find((x) => x.row === r && x.col === c);

  return (
    <div ref={ref} className="viz heatmap">
      <svg width={width} height={height} role="img" aria-label="Average reward by regime and action">
        {cols.map((c, j) => (
          <text key={c} className="tick" x={labelW + j * (cellW + gap) + cellW / 2} y={12} textAnchor="middle">
            {c}
          </text>
        ))}
        {rows.map((r, i) => (
          <g key={r}>
            <text className="tick tick-strong" x={labelW - 10} y={22 + i * (cellH + gap) + cellH / 2} dy="0.32em" textAnchor="end">
              {r}
            </text>
            {cols.map((c, j) => {
              const cell = at(i, j);
              const ok = cell && cell.n >= minN;
              const fill = ok ? diverging(cell.value, maxAbs) : '#12141c';
              const cx = labelW + j * (cellW + gap);
              const cy = 22 + i * (cellH + gap);
              return (
                <g
                  key={c}
                  tabIndex={0}
                  onPointerEnter={() => cell && setHover(cell)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => cell && setHover(cell)}
                  onBlur={() => setHover(null)}
                >
                  <rect x={cx} y={cy} width={cellW} height={cellH} rx={4} fill={fill} />
                  {ok && cellW >= 44 && (
                    <text x={cx + cellW / 2} y={cy + cellH / 2} dy="0.32em" textAnchor="middle" className="cell-label" fill={inkOn(fill)}>
                      {cell.value >= 0 ? '+' : '−'}
                      {Math.abs(cell.value).toFixed(2)}
                    </text>
                  )}
                  {!ok && (
                    <text x={cx + cellW / 2} y={cy + cellH / 2} dy="0.32em" textAnchor="middle" className="tick">
                      ·
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
      <div className="scale-legend">
        <span>worse than 0</span>
        <i style={{ background: `linear-gradient(90deg, ${diverging(-1, 1)}, ${diverging(0, 1)}, ${diverging(1, 1)})` }} />
        <span>better</span>
        <span className="muted">· cells need {minN}+ samples</span>
      </div>
      {hover && (
        <Tooltip x={labelW + hover.col * (cellW + gap) + cellW / 2} y={22 + hover.row * (cellH + gap)} width={width}>
          <strong>
            {hover.value >= 0 ? '+' : '−'}
            {Math.abs(hover.value).toFixed(3)}
          </strong>
          <span className="tt-row">
            {rows[hover.row]} · {cols[hover.col]}
          </span>
          <span className="tt-time">{hover.n} decisions</span>
        </Tooltip>
      )}
    </div>
  );
}
